import { weekdayShortLabel, monthLabel, shouldShowMonthLabel } from '@/utils/timeline';
import type { TimelineDayColumn } from './useTimelineLayout';

interface DayHeaderProps {
  days: TimelineDayColumn[];
  /** Minimum width per day column. Matches `useTimelineLayout.minDayWidth`
   *  so the header and rows always share the same column widths and the
   *  grid scrolls horizontally when the date range is wider than the
   *  viewport (Toggl-style lazy-load trigger — see issue #12). */
  minDayWidth: number;
  /** Total header height (month strip + day row). Matches
   *  `useTimelineLayout.headerHeight` so the labels column's empty
   *  header cell and the overlays line up. */
  headerHeight: number;
  /** Uses lighter separators and tighter labels in the mobile viewport. */
  isCompact?: boolean;
}

/** Height of the month strip row inside the header. */
const MONTH_STRIP_HEIGHT = 24;

/**
 * Sticky header with two rows, Toggl Plan style:
 *  1. Month strip — uppercase month name rendered on the first visible
 *     day and on every day 1 (month boundary), so the user always knows
 *     which month they're looking at.
 *  2. Day row — weekday + day-of-month for every visible day.
 * Sa/Su cells render in muted-foreground to match the existing look.
 */
export function DayHeader({
  days,
  minDayWidth,
  headerHeight,
  isCompact = false,
}: DayHeaderProps) {
  const gridTemplateColumns = `repeat(${days.length}, minmax(${minDayWidth}px, 1fr))`;

  return (
    <div
      data-testid="timeline-day-header"
      // Sticky top-0 keeps the header visible while rows scroll vertically.
      // The matching empty header-cell that pairs with the labels
      // column is rendered inside TimelineGrid's labels column — this
      // component is just the day-cell grid.
      //
      // NO border-b here: the wrapper is only as wide as the scrollport
      // while the day tracks overflow it, so a container border would stop
      // mid-scroll. Each day-header-cell draws its own border-b instead
      // (issue #33 follow-up — same overflow reason as the row tint).
      className="sticky top-0 z-10 bg-background"
      style={{ minHeight: headerHeight }}
    >
      {/* Month strip — label on the first visible column and on every
          month boundary (day 1). Left-aligned so the label starts where
          the month starts, Toggl-style. The border-b is drawn PER CELL —
          a container border would stop at the scrollport width while the
          day tracks overflow it (issue #33). */}
      <div
        data-testid="timeline-month-strip"
        className="grid"
        style={{ gridTemplateColumns, height: MONTH_STRIP_HEIGHT }}
      >
        {days.map((day, index) => {
          const showLabel = shouldShowMonthLabel(day.date, days[index - 1]?.date);
          return (
            <div
              key={day.date}
              data-testid="timeline-month-label-cell"
              data-day={day.date}
              className={`flex items-end overflow-hidden whitespace-nowrap border-b border-border/50 px-1.5 pb-0.5 font-semibold uppercase tracking-wider ${
                isCompact ? 'text-[9px]' : 'text-[10px]'
              } ${
                day.isToday ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {showLabel ? monthLabel(day.date) : null}
            </div>
          );
        })}
      </div>

      {/* Day row — weekday + day-of-month. */}
      <div
        className="grid"
        style={{
          gridTemplateColumns,
          height: headerHeight - MONTH_STRIP_HEIGHT,
        }}
      >
        {days.map((day) => {
          const dayNumber = Number(day.date.slice(-2));
          return (
            <div
              key={day.date}
              data-testid="timeline-day-header-cell"
              data-day={day.date}
              // border-b per cell (issue #33): together the cells draw one
              // continuous bottom line across the FULL scroll width — a
              // container-level border would end at the scrollport width.
              className={`flex items-center justify-center border-b font-medium ${
                isCompact ? 'text-[11px]' : 'text-xs'
              } ${
                day.isWeekend ? 'text-muted-foreground' : 'text-foreground'
              } ${day.isToday ? 'font-semibold text-primary' : ''}`}
            >
              {weekdayShortLabel(day.date)} {dayNumber}
            </div>
          );
        })}
      </div>
    </div>
  );
}
